import type { GetState, SetState } from "zustand";

import type { FormImprovement, FormImprovementState } from "../../types";
import type { AskPerformYardState } from "../types";

/**
 * Form Building Coach slice: the per-improvement state machine plus the bridge to the
 * form builder (`formImprovementApplier` callback in). The form snapshot itself comes from
 * useEditFormStore, so unlike the goal coach there is no snapshot state here.
 */
export const createFormBuildingCoachSlice = (
	set: SetState<AskPerformYardState>,
	get: GetState<AskPerformYardState>
) => ({
	formImprovementStates: [] as FormImprovementState[],
	formImprovementApplier: null as ((improvement: FormImprovement) => boolean | Promise<boolean>) | null,

	setFormImprovementApplier: (fn: ((improvement: FormImprovement) => boolean | Promise<boolean>) | null) =>
		set({ formImprovementApplier: fn }),

	setFormImprovements: (messageId: string, suggestions: FormImprovement[]) => {
		set((state) => {
			const msgIdx = state.messages.findIndex((m) => m.id === messageId);
			const updatedMessages = state.messages.slice();
			if (msgIdx !== -1) {
				updatedMessages[msgIdx] = { ...updatedMessages[msgIdx], formImprovements: suggestions };
			}

			const newStates: FormImprovementState[] = suggestions.map((s) => ({
				suggestion: s,
				status: "pending" as const,
				messageId,
			}));

			// Replace any existing states for this messageId; keep states from other messages.
			const otherStates = state.formImprovementStates.filter((fs) => fs.messageId !== messageId);

			return {
				messages: updatedMessages,
				formImprovementStates: [...otherStates, ...newStates],
			};
		});
	},

	// Guard + mark + bridge in one action: only a pending suggestion applies, and only while
	// the form builder is mounted. Marked applied optimistically (the applier can be async —
	// it may auto-save an open card first — and repeat clicks must not double-apply), then
	// reverted to pending when the applier reports failure or throws (open card failed
	// validation, or the target question no longer exists) so the user can retry.
	applyFormImprovement: (suggestionId: string) => {
		const { formImprovementStates, formImprovementApplier } = get();
		const target = formImprovementStates.find((fs) => fs.suggestion.id === suggestionId);
		// A restored suggestion never applies, even with the builder open: its
		// `questionId` is a refId in the form that was on screen when it was
		// written, which in another form either misses or hits an unrelated question.
		if (!target || target.isRestored || target.status !== "pending" || !formImprovementApplier) return;
		const setStatus = (status: FormImprovementState["status"]) =>
			set((state) => ({
				formImprovementStates: state.formImprovementStates.map((fs) =>
					fs.suggestion.id === suggestionId ? { ...fs, status } : fs
				),
			}));
		setStatus("applied");
		void Promise.resolve(formImprovementApplier(target.suggestion))
			.then((applied) => {
				if (!applied) setStatus("pending");
			})
			.catch(() => setStatus("pending"));
	},

	markFormImprovementDeleted: (suggestionId: string) => {
		set((state) => ({
			formImprovementStates: state.formImprovementStates.map((fs) =>
				fs.suggestion.id === suggestionId ? { ...fs, status: "deleted" as const } : fs
			),
		}));
	},
});
